import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import paymentporterService from '../services/paymentporterService';
import ppProcessorService from '../services/ppProcessorService';
import './PaymentPorterPage.css';
import './PaymentPorterProcessorPage.css';
import { formatDateTime } from '../utils/datetime';

const statusBadge = (status) => {
    if (!status) return null;
    const s = (status || '').toUpperCase();
    const cls =
        s === 'RUNNING'           ? 'ppp-badge-running'   :
        s === 'COMPLETED'         ? 'ppp-badge-done'      :
        s === 'FAILED'            ? 'ppp-badge-failed'    :
        s === 'INITIALISING'      ? 'ppp-badge-init'      :
        s === 'SUCCESS'           ? 'ppp-badge-success'   :
        s === 'ERROR'             ? 'ppp-badge-error'     :
        s === 'SKIPPED'           ? 'ppp-badge-skipped'   :
        s === 'QUALIFIED'         ? 'ppp-badge-qualified' :
        s === 'DRY_RUN_QUALIFIED' ? 'ppp-badge-dry'       : 'ppp-badge-init';
    return <span className={`ppp-badge ${cls}`}>{status}</span>;
};

const fmtDate = (iso) => {
    if (!iso) return '—';
    return formatDateTime(iso);
};

const RunDetail = ({ ppId, runUuid }) => {
    const [records, setRecords]   = useState(null);
    const [loading, setLoading]   = useState(true);
    const [error, setError]       = useState(null);

    useEffect(() => {
        setLoading(true);
        ppProcessorService.getRunDetail(ppId, runUuid)
            .then(res => setRecords(res.data?.records || []))
            .catch(() => setError('Failed to load reservation detail'))
            .finally(() => setLoading(false));
    }, [ppId, runUuid]);

    if (loading) return <div className="ppp-loading">Loading reservations...</div>;
    if (error)   return <div className="ppp-error">{error}</div>;
    if (!records?.length) return <div className="ppp-empty">No reservation records found for this run.</div>;

    return (
        <div className="ppp-table-wrap">
            <table className="ppp-table">
                <thead>
                    <tr>
                        <th>Confirmation</th>
                        <th>Reservation ID</th>
                        <th>Status</th>
                        <th>Matched Criteria</th>
                        <th>Error / Skip Reason</th>
                        <th>Updated</th>
                    </tr>
                </thead>
                <tbody>
                    {records.map((r, i) => (
                        <tr key={i}>
                            <td>{r.confirmationNumber || '—'}</td>
                            <td>{r.reservationId || '—'}</td>
                            <td>{statusBadge(r.status)}</td>
                            <td style={{ maxWidth: 260, fontSize: '0.75rem' }}>{(r.matchedCriteria || []).join(', ') || '—'}</td>
                            <td style={{ maxWidth: 200, fontSize: '0.75rem', color: r.errorMessage ? '#dc2626' : r.skipReason ? '#92400e' : undefined }}>
                                {r.errorMessage || r.skipReason || '—'}
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.updatedAt)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const PaymentPorterProcessorPage = () => {
    const navigate = useNavigate();
    const { ppId } = useParams();

    const [pp, setPp]                   = useState(null);
    const [runs, setRuns]               = useState([]);
    const [runsLoading, setRunsLoading] = useState(true);
    const [expandedRun, setExpandedRun] = useState(null);

    const loadPp = useCallback(() => {
        paymentporterService.getById(ppId)
            .then(res => setPp(res.data?.paymentPorter))
            .catch(() => {});
    }, [ppId]);

    const loadRuns = useCallback(() => {
        setRunsLoading(true);
        ppProcessorService.listRuns(ppId)
            .then(res => setRuns(res.data?.runs || []))
            .catch(() => {})
            .finally(() => setRunsLoading(false));
    }, [ppId]);

    useEffect(() => {
        loadPp();
        loadRuns();
    }, [loadPp, loadRuns]);

    return (
        <div className="ppp-page">
            <button className="ppp-back-btn" onClick={() => navigate(`/apps/payment-porter/${ppId}`)}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {pp?.name || 'Payment Porter'}
            </button>

            <div className="ppp-header">
                <h1 className="ppp-title">Processor</h1>
                <p className="ppp-subtitle">Schedule automatic runs and review processing history</p>
            </div>

            {/* Run history */}
            <div className="ppp-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <div className="ppp-section-title" style={{ margin: 0 }}>Run History</div>
                    <button className="pp-refresh-btn" onClick={loadRuns}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.5"/></svg>
                        Refresh
                    </button>
                </div>

                {runsLoading ? (
                    <div className="ppp-loading">Loading runs...</div>
                ) : runs.length === 0 ? (
                    <div className="ppp-empty">No runs yet. Use the buttons above to start your first run.</div>
                ) : (
                    <div className="ppp-table-wrap">
                        <table className="ppp-table">
                            <thead>
                                <tr>
                                    <th>Started</th>
                                    <th>Type</th>
                                    <th>Status</th>
                                    <th>Fetched</th>
                                    <th>Qualified</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {runs.map(run => (
                                    <React.Fragment key={run.runUuid}>
                                        <tr>
                                            <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(run.startedAt)}</td>
                                            <td>{run.dryRun ? <span className="ppp-badge ppp-badge-dry">Dry Run</span> : <span className="ppp-badge ppp-badge-done">Live</span>}</td>
                                            <td>{statusBadge(run.status)}</td>
                                            <td>{run.totalFetched ?? '—'}</td>
                                            <td>{run.totalQualified ?? '—'}</td>
                                            <td>
                                                <button
                                                    className="ppp-detail-btn"
                                                    onClick={() => setExpandedRun(expandedRun === run.runUuid ? null : run.runUuid)}
                                                >
                                                    {expandedRun === run.runUuid ? 'Hide' : 'Details'}
                                                </button>
                                            </td>
                                        </tr>
                                        {expandedRun === run.runUuid && (
                                            <tr>
                                                <td colSpan={6} style={{ padding: 0 }}>
                                                    <div className="ppp-detail-panel">
                                                        <div className="ppp-detail-title">Reservation Records — {run.runUuid}</div>
                                                        <RunDetail ppId={ppId} runUuid={run.runUuid} />
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentPorterProcessorPage;
